/**
 * Action Renderer - For Ops / Workflow Agents
 * Used when: output.action_payload[] exists
 * 
 * Features:
 * - NO collapsible sections
 * - Checklists and action rows
 * - Inline CTAs (Create Automation, Assign, Export)
 * - Emphasizes what to do next, not prose
 * - Clear action grouping
 */

import React, { useState } from 'react';
import {
  ArrowRight,
  ArrowSquareOut,
  Calendar,
  CheckCircle,
  CheckSquare,
  Circle,
  Clock,
  FileText,
  Lightning,
  PaperPlaneRight,
  Play,
  Square,
  User,
  WarningCircle
} from '@phosphor-icons/react';

// Priority styling
const getPriorityStyle = (priority) => {
  switch (priority?.toLowerCase()) {
    case 'high':
    case 'critical':
      return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', label: 'High' };
    case 'medium':
      return { bg: 'bg-yellow-500/10', border: 'border-yellow-500/30', text: 'text-yellow-400', label: 'Medium' };
    case 'low':
      return { bg: 'bg-gray-500/10', border: 'border-gray-500/30', text: 'text-gray-400', label: 'Low' };
    default:
      return { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', label: 'Normal' };
  }
};

// Status styling
const getStatusStyle = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
    case 'done':
      return { icon: CheckCircle, color: 'text-green-400' };
    case 'in_progress':
    case 'pending':
      return { icon: Clock, color: 'text-yellow-400' };
    case 'blocked':
    case 'failed':
      return { icon: WarningCircle, color: 'text-red-400' };
    default:
      return { icon: Circle, color: 'text-gray-400' };
  }
};

export const ActionRenderer = ({ content, outputData, onAction }) => {
  const [checkedItems, setCheckedItems] = useState({});
  
  // Extract action payload
  const actionPayload = outputData?.action_payload || content?.action_payload || {};
  const actions = Array.isArray(actionPayload) ? actionPayload : (actionPayload.actions || []);
  const summary = actionPayload.summary || content?.summary;
  
  // Group actions by category/type
  const groupedActions = actions.reduce((acc, action, idx) => {
    const group = action.category || action.group || action.type || 'Actions';
    if (!acc[group]) acc[group] = [];
    acc[group].push({ ...action, _idx: idx });
    return acc;
  }, {});
  
  const toggleCheck = (idx) => {
    setCheckedItems(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };
  
  // Render a single action item
  const renderActionItem = (action, idx) => {
    const isChecked = checkedItems[idx];
    const priority = getPriorityStyle(action.priority);
    const status = getStatusStyle(action.status);
    const StatusIcon = status.icon;
    
    return (
      <div 
        key={idx}
        className={`p-4 rounded-lg border transition-all ${
          isChecked 
            ? 'bg-[#14F195]/5 border-[#14F195]/30' 
            : 'bg-[#111111] border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)]'
        }`}
      >
        <div className="flex items-start gap-3">
          {/* Checkbox */}
          <button 
            onClick={() => toggleCheck(idx)}
            className="mt-0.5 flex-shrink-0"
          >
            {isChecked ? (
              <CheckSquare size={18} className="text-[#14F195]" />
            ) : (
              <Square size={18} className="text-gray-500 hover:text-gray-300" />
            )}
          </button>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {/* Action title */}
              <span className={`font-medium ${isChecked ? 'text-gray-400 line-through' : 'text-white'}`}>
                {action.label || action.title || action.description || `Action ${idx + 1}`}
              </span>
              
              {/* Priority badge */}
              {action.priority && (
                <span className={`text-xs px-2 py-0.5 rounded ${priority.bg} ${priority.text} border ${priority.border}`}>
                  {priority.label}
                </span>
              )}
              
              {/* Status indicator */}
              {action.status && (
                <StatusIcon size={14} className={status.color} />
              )}
            </div>
            
            {/* Description */}
            {action.description && action.label && (
              <p className={`text-sm ${isChecked ? 'text-gray-500' : 'text-gray-400'}`}>
                {action.description}
              </p>
            )}
            
            {/* Meta info */}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              {action.assignee && (
                <span className="flex items-center gap-1">
                  <User size={12} />
                  {action.assignee}
                </span>
              )}
              {action.due_date && (
                <span className="flex items-center gap-1">
                  <Calendar size={12} />
                  {action.due_date}
                </span>
              )}
              {action.estimated_time && (
                <span className="flex items-center gap-1">
                  <Clock size={12} />
                  {action.estimated_time}
                </span>
              )}
            </div>
          </div>
          
          {/* Quick action button */}
          {action.action_url && (
            <a 
              href={action.action_url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)] rounded-lg transition-colors"
            >
              <ArrowSquareOut size={14} />
            </a>
          )}
        </div>
      </div>
    );
  };
  
  // Progress indicator
  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = actions.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  
  return (
    <div className="space-y-6">
      {/* Summary if available */}
      {summary && (
        <div className="p-4 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded-lg">
          <p className="text-sm text-gray-300">{summary}</p>
        </div>
      )}
      
      {/* Progress bar */}
      {totalCount > 0 && (
        <div className="flex items-center gap-4">
          <div className="flex-1 h-2 bg-[rgba(255,255,255,0.1)] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#14F195] transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-sm text-gray-400">
            {completedCount}/{totalCount} completed
          </span>
        </div>
      )}
      
      {/* Grouped action lists */}
      {Object.entries(groupedActions).map(([group, groupActions]) => (
        <div key={group}>
          {Object.keys(groupedActions).length > 1 && (
            <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide mb-3">
              {group}
            </h3>
          )}
          <div className="space-y-2">
            {groupActions.map((action) => renderActionItem(action, action._idx))}
          </div>
        </div>
      ))}
      
      {/* No actions fallback */}
      {actions.length === 0 && (
        <div className="p-6 text-center">
          <CheckCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400">No pending actions</p>
        </div>
      )}
      
      {/* Action bar */}
      {actions.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-4 border-t border-[rgba(255,255,255,0.08)]">
          <button 
            onClick={() => onAction?.('create_automation')}
            className="px-4 py-2 text-sm bg-[#6366F1] text-white rounded-lg hover:bg-[#5558E3] transition-colors flex items-center gap-2"
          >
            <Lightning size={14} />
            Automate These Actions
          </button>
          <button 
            onClick={() => onAction?.('assign')}
            className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
          >
            <User size={14} />
            Assign
          </button>
          <button 
            onClick={() => onAction?.('export')}
            className="px-4 py-2 text-sm bg-[rgba(255,255,255,0.05)] text-gray-300 border border-[rgba(255,255,255,0.1)] rounded-lg hover:bg-[rgba(255,255,255,0.08)] transition-colors flex items-center gap-2"
          >
            <FileText size={14} />
            Export
          </button>
        </div>
      )}
      
      {/* Ready message */}
      <p className="text-xs text-gray-500 text-center">
        These actions are ready for execution.
      </p>
    </div>
  );
};

export default ActionRenderer;
